import {z} from "zod/v4-mini"
import {graphNode} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {ConstOperatorTypeOption} from "#template/nodes/geometry-graph/options"
import {constOperatorTypeOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef, ValueRef} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type ConstOperatorParameters<TAttribute> = {
    name: string
    attribute: TAttribute | null
    value: GeometryValueNode | NumberLike | null
    type: ConstOperatorTypeOption
}

export const constOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        attribute: z.nullable(attributeSchema),
        value: z.nullable(z.union([graphNode(GeometryValueNode), numberLike])),
        type: constOperatorTypeOption,
    })

function applyConstOperator<T extends IndexingDim>(
    attribute: GeometryAttributeData<T>,
    value: GeometryValueData | number,
    typeOption: ConstOperatorTypeOption,
): GeometryAttributeData<T> {
    const attrRefData = attribute.ref
    const valueRefData = resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber(value)

    const constAttrRefData = applyGeometryOperator(
        (attrRef: AttributeRef, value: ValueRef | number) => {
            switch (typeOption) {
                case "float":
                    return attrRef.constFloat(value)
                case "int":
                    return attrRef.constInt(value)
                case "like":
                    return attrRef.constLike(value)
                default:
                    throw new Error(`Unknown const operator type option: ${typeOption}`)
            }
        },
        attrRefData,
        valueRefData,
    )

    return createGeometryAttributeData(constAttrRefData, attribute.dim)
}

export abstract class GeometryConstOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, ConstOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: ConstOperatorParameters<TAttribute>,
    ): parameters is ConstOperatorParameters<TAttribute> & NonNullableParameters<ConstOperatorParameters<TAttribute>> {
        return parameters.attribute !== null && parameters.value !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<ConstOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attribute, value, type: typeOption} = parameters

        const [evaluatedAttributeValid, evaluatedAttributeInvalid] = scope.branch(evaluator.evaluate(attribute))
        const [evaluatedValueValid, evaluatedValueInvalid] = scope.branch(evaluator.evaluate(value))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedAttributeValid, evaluatedValueValid, typeOption),
            ([attribute, value, typeOption]) => applyConstOperator(attribute, value, typeOption),
            "applyConstOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(evaluatedAttributeInvalid, evaluatedValueInvalid)}
    }
}
